import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { setCurrentTab, showSideBar, showNavBar } from '../../actions/commonActions'
import { getOrganizations, createOrganization, updateOrganization } from '../../actions/organizationsActions'
import { START_PAGE_DEFAULT, PER_PAGE_DEFAULT } from '../../misc/Constants'

import Loader from '../common/loader/Loader'
import TablePlaceholder from '../common/TablePlaceholder'
import PageHeader from '../common/PageHeader/PageHeader'
import OrganizationsTable from './OrganizationsTable'
import OrganizationDrawer from './OrganizationDrawer'
import OrganizationFormModal from './OrganizationFormModal'

import classes from './OrganizationsPage.module.scss'

const OrganizationsPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const organizations = useSelector(state => state.organizations.organizations)
  const pagination = useSelector(state => state.organizations.pagination)

  const [loader, setLoader] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isSortLoading, setIsSortLoading] = useState(false)
  const [sortOption, setSortOption] = useState({ sortField: null, sortValue: null })
  const [inspectedOrganizationId, setInspectedOrganizationId] = useState(null)
  const [formOrganization, setFormOrganization] = useState(null)
  const [showFormModal, setShowFormModal] = useState(false)

  const inspectedOrganization = organizations.find(organization => organization.id === inspectedOrganizationId)

  const fetchOrganizations = useCallback((page = START_PAGE_DEFAULT, append = false) => {
    const sort = { sortField: sortOption.sortValue ? sortOption.sortField : null, sortValue: sortOption.sortValue }

    setIsLoading(true)
    return dispatch(getOrganizations({ page, perPage: PER_PAGE_DEFAULT, append, ...sort })).unwrap()
      .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => {
        setLoader(false)
        setIsLoading(false)
        setIsSortLoading(false)
      })
  }, [dispatch, t, sortOption])

  useEffect(() => {
    dispatch(setCurrentTab('Organizations'))
    dispatch(showSideBar(true))
    dispatch(showNavBar(true))
  }, [dispatch])

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  const handleSort = (sortField, sortValue) => {
    setIsSortLoading(true)
    setSortOption({ sortField, sortValue })
  }

  const openForm = (organization = null) => {
    setFormOrganization(organization)
    setShowFormModal(true)
  }

  const handleSubmit = (name) => {
    const request = formOrganization ? updateOrganization({ id: formOrganization.id, name }) : createOrganization(name)

    return dispatch(request).unwrap().then(() => {
      setShowFormModal(false)
      const successKey = formOrganization ? 'organizations.page.updateSuccess' : 'organizations.page.createSuccess'
      toast.success(t(successKey), { position: toast.POSITION.TOP_RIGHT })
      if (!formOrganization) { fetchOrganizations() }
    })
  }

  const addOrganizationButton = (
    <button className='btn btn-primary text-white pull-right mr-2' onClick={() => openForm()}>
      {t('organizations.page.addOrganization')}
    </button>
  )

  return (
    <div id='organizationsPage'>
      <PageHeader title={t('organizations.page.title')} actionComponent={addOrganizationButton} />

      <main className={classes.wrapper}>
        {loader ? (
          <div className='h-100 d-flex align-items-center justify-content-center'>
            <Loader>{t('organizations.page.loading')}</Loader>
          </div>
        ) : organizations.length === 0 ? (
          <TablePlaceholder message={t('organizations.table.noOrganizationsFound')} />
        ) : (
          <OrganizationsTable
            organizations={organizations}
            organizationsCount={pagination.totalCount || 0}
            hasMore={pagination.isLastPage === false}
            isLoading={isLoading}
            isSortLoading={isSortLoading}
            sortOption={sortOption}
            onSort={handleSort}
            onFetchMore={() => fetchOrganizations(pagination.page + 1, true)}
            onRowClick={(organization) => setInspectedOrganizationId(organization.id)}
            selectedOrganizationId={inspectedOrganizationId}
          />
        )}
      </main>

      {inspectedOrganization && (
        <OrganizationDrawer
          organization={inspectedOrganization}
          onClose={() => setInspectedOrganizationId(null)}
          onEdit={() => openForm(inspectedOrganization)}
        />
      )}

      {showFormModal && (
        <OrganizationFormModal
          isOpen={showFormModal}
          organization={formOrganization}
          onClose={() => setShowFormModal(false)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  )
}

export default OrganizationsPage
